import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const releaseReasonKeys = {
  all: ['releaseReasons'],
  lists: () => [...releaseReasonKeys.all, 'list'],
  details: () => [...releaseReasonKeys.all, 'detail'],
  detail: (id) => [...releaseReasonKeys.details(), id],
};

/**
 * Hook para obtener todas las razones de liberación
 */
export const useReleaseReasons = () => {
  return useQuery({
    queryKey: releaseReasonKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('release_reasons');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

/**
 * Hook para obtener una razón de liberación específica
 * @param {string} id - UUID de la razón de liberación
 * @param {Object} options - Opciones adicionales para la query
 */
export const useReleaseReason = (id, options = {}) => {
  return useQuery({
    queryKey: releaseReasonKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`release_reasons/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para crear una nueva razón de liberación
 */
export const useCreateReleaseReason = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (releaseReasonData) => {
      const response = await apiClient.post('release_reasons', releaseReasonData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: releaseReasonKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating release reason:', error);
    },
  });
};

/**
 * Hook para actualizar una razón de liberación existente
 */
export const useUpdateReleaseReason = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (releaseReasonData) => {
      const response = await apiClient.put(`release_reasons/${releaseReasonData.uuid}`, releaseReasonData);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(releaseReasonKeys.detail(data.uuid), data);
      queryClient.invalidateQueries({ queryKey: releaseReasonKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating release reason:', error);
    },
  });
};

/**
 * Hook para eliminar una razón de liberación
 */
export const useDeleteReleaseReason = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`release_reasons/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: releaseReasonKeys.lists() });
      queryClient.removeQueries({ queryKey: releaseReasonKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting release reason:', error);
    },
  });
};